import { useState, type FormEvent } from 'react';
import { Plus } from 'lucide-react';
import { ConfirmDeleteButton } from '../components/confirm-delete-button';
import { toast } from 'sonner';
import { useAreas, useCreateArea, useDeleteArea, type Area } from '../lib/hooks';
import { useT } from '../lib/i18n';
import { Modal } from '../components/modal';

const TYPES: Array<{ value: Area['type']; label: string }> = [
  { value: 'job', label: 'Job' },
  { value: 'business', label: 'Business' },
  { value: 'brand', label: 'Brand' },
  { value: 'location', label: 'Location' },
  { value: 'project', label: 'Project' },
  { value: 'personal', label: 'Personal' },
];

export function AreasPage() {
  const t = useT();
  const areas = useAreas();
  const create = useCreateArea();
  const remove = useDeleteArea();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [type, setType] = useState<Area['type']>('project');
  const [parentAreaId, setParentAreaId] = useState('');
  const [color, setColor] = useState('#6366f1');

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    await create.mutateAsync({
      name: name.trim(),
      type,
      parentAreaId: parentAreaId || null,
      color,
    });
    setName('');
    setType('project');
    setParentAreaId('');
    setColor('#6366f1');
    setOpen(false);
    toast.success('Đã tạo area');
  };

  const tops = areas.data?.filter((a) => !a.parentAreaId) ?? [];
  const childrenOf = (id: string) => areas.data?.filter((a) => a.parentAreaId === id) ?? [];

  return (
    <div className="mx-auto max-w-7xl space-y-6 p-6 lg:p-8">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-slate-900 dark:text-slate-100">
            {t('areas.title')}
          </h1>
          <p className="mt-1 text-sm text-slate-500">
            Phân vùng theo PARA — task và note đều gắn vào 1 Area để focus
          </p>
        </div>
        <button type="button" className="btn-primary" onClick={() => setOpen(true)}>
          <Plus className="h-4 w-4" />
          {t('areas.add')}
        </button>
      </header>

      {areas.isLoading ? (
        <p className="text-sm text-slate-500">{t('common.loading')}</p>
      ) : tops.length > 0 ? (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          {tops.map((a) => {
            const kids = childrenOf(a.id);
            return (
              <div key={a.id} className="panel group relative overflow-hidden">
                <div
                  className="absolute -top-12 -right-12 h-32 w-32 rounded-full opacity-20 blur-3xl"
                  style={{ backgroundColor: a.color ?? '#64748b' }}
                />
                <div className="absolute top-3 right-3 z-10 opacity-100 md:opacity-60 md:group-hover:opacity-100 transition">
                  <ConfirmDeleteButton
                    size="md"
                    label="Xoá area"
                    disabled={remove.isPending}
                    onConfirm={() => {
                      remove.mutate(a.id, {
                        onSuccess: () => toast.success(`Đã xoá area "${a.name}"`),
                        onError: (e) =>
                          toast.error(e instanceof Error ? e.message : 'Không xoá được'),
                      });
                    }}
                  />
                </div>
                <div className="panel-body relative space-y-3">
                  <div className="flex items-center gap-3 pr-8">
                    <span
                      className="h-3 w-3 rounded-full"
                      style={{ backgroundColor: a.color ?? '#64748b' }}
                    />
                    <span className="text-base font-semibold text-slate-900 dark:text-slate-100">
                      {a.name}
                    </span>
                    <span className="badge">{a.type}</span>
                    <span className="badge-info">{a.contextMode}</span>
                  </div>
                  {kids.length > 0 && (
                    <ul className="space-y-1.5 border-l-2 border-slate-200 dark:border-white/5 pl-4">
                      {kids.map((k) => (
                        <li key={k.id} className="group/kid flex items-center gap-2 text-sm">
                          <span
                            className="h-2 w-2 rounded-full"
                            style={{ backgroundColor: k.color ?? '#64748b' }}
                          />
                          <span className="text-slate-700 dark:text-slate-300">{k.name}</span>
                          <span className="badge">{k.type}</span>
                          <div className="ml-auto">
                            <ConfirmDeleteButton
                              label="Xoá"
                              disabled={remove.isPending}
                              onConfirm={() => {
                                remove.mutate(k.id, {
                                  onSuccess: () => toast.success(`Đã xoá "${k.name}"`),
                                  onError: (e) =>
                                    toast.error(
                                      e instanceof Error ? e.message : 'Không xoá được',
                                    ),
                                });
                              }}
                            />
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="panel">
          <div className="panel-body py-12 text-center text-sm text-slate-500">
            {t('areas.empty')}
          </div>
        </div>
      )}

      <Modal
        open={open}
        onClose={() => setOpen(false)}
        title={t('areas.add')}
        footer={
          <>
            <button type="button" onClick={() => setOpen(false)} className="btn-secondary">
              {t('common.cancel')}
            </button>
            <button
              type="submit"
              form="area-form"
              className="btn-primary"
              disabled={create.isPending}
            >
              {t('common.save')}
            </button>
          </>
        }
      >
        <form id="area-form" onSubmit={onSubmit} className="space-y-4">
          <div>
            <label className="label">Tên</label>
            <input
              className="input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              autoFocus
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label">Loại</label>
              <select
                className="input"
                value={type}
                onChange={(e) => setType(e.target.value as Area['type'])}
              >
                {TYPES.map((tt) => (
                  <option key={tt.value} value={tt.value} className="bg-slate-900">
                    {tt.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="label">Màu</label>
              <input
                type="color"
                className="h-10 w-full cursor-pointer rounded-xl border border-slate-200 dark:border-white/10 bg-transparent"
                value={color}
                onChange={(e) => setColor(e.target.value)}
              />
            </div>
          </div>
          <div>
            <label className="label">Area cha (tuỳ chọn)</label>
            <select
              className="input"
              value={parentAreaId}
              onChange={(e) => setParentAreaId(e.target.value)}
            >
              <option value="" className="bg-slate-900">
                — Không có cha —
              </option>
              {areas.data?.map((a) => (
                <option key={a.id} value={a.id} className="bg-slate-900">
                  {a.name}
                </option>
              ))}
            </select>
          </div>
        </form>
      </Modal>
    </div>
  );
}
